/**
 * Prijsoverzicht (US-010, US-021, US-022, US-033, design-handoff scherm 4).
 *
 * De matrix zet producten tegen webshops, met de eigen webshop op een vaste eerste positie.
 * Gewijzigde prijzen springen eruit, ongewijzigde blijven visueel ondergeschikt. Betekenis
 * hangt nooit alleen aan kleur: er staat altijd een teken en een tekst bij (US-021, laatste
 * criterium).
 */

import {
  formatEuro,
  mediaan,
  MEASUREMENT_STATUS_LABELS,
  sorteerOpPrijs,
  type MeasurementStatus,
} from '@prijsmonitor/shared';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { api, type ApiError } from '../api/client.js';
import { BronLabel } from '../components/BronLabel.js';
import { VersusEigen } from '../components/VersusEigen.js';
import { Button, Counter, Notice, ui } from '../components/ui/index.js';
import styles from './OverviewScreen.module.css';

interface Cell {
  sourceId: string;
  linked: boolean;
  priceCents: number | null;
  previousValidCents: number | null;
  changeCents: number | null;
  changeDirection: 'NONE' | 'UP' | 'DOWN';
  isChange: boolean;
  status: MeasurementStatus | null;
  statusDetail: string | null;
  percentVsOwn: number | null;
  /** De laagste geldige prijs van deze rij; dezelfde markering als in de prijshistorie. */
  isLowest: boolean;
  hasEvidence: boolean;
}

interface Row {
  productId: string;
  name: string;
  ownSku: string;
  lengthLabel: string | null;
  color: string | null;
  cells: Cell[];
  changeCount: number;
  errorCount: number;
}

interface Overview {
  dateKey: string;
  columns: Array<{
    sourceId: string;
    name: string;
    domain: string | null;
    isOwn: boolean;
    url?: string | null;
  }>;
  products: Row[];
  kpi: { productsActive: number; sourcesActive: number; changes: number; missing: number };
  lastRun: { startedAt: string | null; finishedAt: string | null; status: string } | null;
}

/** Eén cel in de matrix. */
function MatrixCell({ cell, isOwn }: { cell: Cell; isOwn: boolean }): React.JSX.Element {
  if (!cell.linked) {
    return (
      <td className={isOwn ? styles.ownCell : undefined}>
        <span className={styles.notLinked}>niet gekoppeld</span>
      </td>
    );
  }

  // Een lege meting is geen wijziging; die krijgt een eigen tint en tekst (US-017/021).
  if (cell.priceCents === null) {
    return (
      <td className={`${styles.cellEmpty} ${isOwn ? styles.ownCell : ''}`}>
        <div className={styles.emptyLabel}>— leeg</div>
        <div className={styles.sub}>
          {cell.status ? MEASUREMENT_STATUS_LABELS[cell.status] : 'Nog niet gemeten'}
        </div>
      </td>
    );
  }

  const omhoog = cell.isChange && cell.changeDirection === 'UP';
  const omlaag = cell.isChange && cell.changeDirection === 'DOWN';

  /*
   * De laagste prijs gaat vóór de wijzigingstint, net als in de prijshistorie. Een prijs die
   * gewijzigd is én de laagste, is vooral interessant omdát hij de laagste is; de wijziging blijft
   * zichtbaar aan de pijl en het bedrag eronder.
   */
  const tint = cell.isLowest
    ? styles.cellLowest
    : omhoog
      ? styles.cellUp
      : omlaag
        ? styles.cellDown
        : '';

  return (
    <td className={`${tint} ${isOwn ? styles.ownCell : ''}`}>
      <div className={styles.price}>
        {formatEuro(cell.priceCents)}
        {cell.isLowest && <span className={styles.lowestTag}>laagste</span>}
      </div>
      {cell.isChange && cell.changeCents !== null && (
        <div className={omhoog ? styles.deltaUp : styles.deltaDown}>
          {omhoog ? '▲' : '▼'} {formatEuro(Math.abs(cell.changeCents))} {omhoog ? 'hoger' : 'lager'}
        </div>
      )}
      {/*
        Het verschil met onze prijs staat er nu ook op een dag waarop de prijs is gewijzigd. Dat was
        niet zo: de wijziging verdrong de vergelijking, en juist op zo'n dag is "en waar staan ze nu
        ten opzichte van ons" de eerste vraag.
      */}
      <div className={styles.sub}>
        {isOwn ? 'referentieprijs' : <VersusEigen procent={cell.percentVsOwn} metToelichting />}
      </div>
    </td>
  );
}

export function OverviewScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const zoek = params.get('zoek') ?? '';
  const segment = params.get('segment') ?? 'alle';
  const opNaam = params.get('sortering') === 'naam';

  /*
   * De kolommen op prijs, goedkoopste concurrent vooraan.
   *
   * Hier staat één kolommenrij boven álle producten, en de goedkoopste bron verschilt per product —
   * "de prijs van vandaag" is dus geen enkel getal. Wat wél één getal is: de mediaan van de afwijking
   * ten opzichte van onze prijs over de producten. Die is in de praktijk opvallend stabiel — een bron
   * die structureel onder ons zit, zit dat bij vrijwel elk product — dus de rijen lopen daarna van
   * links naar rechts vrijwel oplopend, en de indeling blijft van dag tot dag hetzelfde.
   *
   * De mediaan en niet het gemiddelde: één product waar een bron uitzonderlijk duur is, hoort de
   * plaats van die kolom niet te bepalen.
   *
   * Er wordt op index gesorteerd en niet op `sourceId`, en dat is geen omslachtigheid maar noodzaak.
   * Een kolom in dit overzicht staat voor een webshop over álle producten, en elk product heeft zijn
   * eigen bronrecord voor die webshop — de API groepeert ze op domein. De `sourceId` van een cel is
   * dus die van het product dat de kolom als eerste introduceerde en klopt bij de andere rijen niet.
   * De afspraak die wél overal geldt is dat `cells` in de volgorde van `columns` staat, dus wordt die
   * volgorde als geheel opnieuw geschikt.
   *
   * Dit is precies zo misgegaan: op `sourceId` opzoeken leverde een overzicht op waarin de eerste rij
   * klopte en alle andere rijen leeg waren.
   */
  const ordening = useMemo(() => {
    if (!data) return [];
    const indexen = data.columns.map((_, index) => index);
    if (opNaam) return indexen;

    const medianen = indexen.map((index) =>
      mediaan(
        data.products
          .map((row) => row.cells[index]?.percentVsOwn)
          .filter((p): p is number => p !== null && p !== undefined),
      ),
    );

    return sorteerOpPrijs(
      indexen,
      (index) => data.columns[index]?.isOwn ?? false,
      (index) => medianen[index] ?? null,
    );
  }, [data, opNaam]);

  useEffect(() => {
    const query = new URLSearchParams();
    if (zoek) query.set('zoek', zoek);
    if (segment !== 'alle') query.set('segment', segment);

    api
      .get<Overview>(`/api/overzicht?${query.toString()}`)
      .then(setData)
      .catch((e: ApiError) => setError(e.message));
  }, [zoek, segment]);

  function setFilter(next: { zoek?: string; segment?: string; sortering?: string }): void {
    const query = new URLSearchParams(params);
    if (next.zoek !== undefined) {
      if (next.zoek) query.set('zoek', next.zoek);
      else query.delete('zoek');
    }
    if (next.segment !== undefined) {
      if (next.segment !== 'alle') query.set('segment', next.segment);
      else query.delete('segment');
    }
    // Op prijs is de standaard; die hoeft niet in het adres te staan.
    if (next.sortering !== undefined) {
      if (next.sortering === 'naam') query.set('sortering', 'naam');
      else query.delete('sortering');
    }
    setParams(query, { replace: true });
  }

  async function exporteer(): Promise<void> {
    setExporting(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      if (zoek) query.set('zoek', zoek);
      if (segment !== 'alle') query.set('segment', segment);

      const response = await fetch(`/api/export/overzicht?${query.toString()}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error?.message ?? 'De export kon niet worden gemaakt.');
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download =
        response.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ??
        'prijsmonitor.xlsx';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'De export kon niet worden gemaakt.');
    } finally {
      setExporting(false);
    }
  }

  if (error && !data)
    return (
      <div className={ui.page}>
        <Notice tone="error">{error}</Notice>
      </div>
    );
  if (!data) {
    return (
      <div className={ui.page}>
        <p className={ui.hint}>Bezig met laden…</p>
      </div>
    );
  }

  /*
   * Waarschuwen wanneer de laatste ronde te oud is (US-016).
   *
   * Dit hoort in dít scherm en niet in de e-mail, en daar is een dwingende reden voor: de dagmail
   * komt uit de ronde zelf. Heeft die niet gelopen, dan is er ook geen mail — een gemiste nacht kan
   * zichzelf dus niet melden. Hier kijk je toch al, en hier doet een verouderd cijfer zijn schade:
   * je vergelijkt met de laatst geldige meting, dus een ontbrekende dag verschuift stil je
   * referentie.
   *
   * De grens staat op 36 uur: ruim genoeg dat een ronde die om 00:15 wat later begon geen valse
   * melding geeft, en kort genoeg dat één gemiste nacht opvalt.
   */
  const laatsteRonde = data.lastRun?.finishedAt ?? data.lastRun?.startedAt ?? null;
  const urenGeleden = laatsteRonde
    ? (Date.now() - new Date(laatsteRonde).getTime()) / 3_600_000
    : null;
  const verouderd = urenGeleden === null || urenGeleden > 36;

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}

      {verouderd && (
        <Notice tone="warn">
          {laatsteRonde
            ? `De laatste controle liep ${Math.floor((urenGeleden ?? 0) / 24)} dag(en) geleden ` +
              `(${new Date(laatsteRonde).toLocaleString('nl-NL')}). De prijzen hieronder zijn dus ` +
              'niet van vannacht.'
            : 'Er heeft nog geen controle gelopen; de prijzen hieronder zijn nog niet gemeten.'}{' '}
          Kijk in Taken &amp; logboek wat er is gebeurd, of start de controle daar opnieuw.
        </Notice>
      )}

      <div className={ui.counterRow}>
        <Counter value={data.kpi.productsActive} label="Producten actief" />
        <Counter value={data.kpi.sourcesActive} label="Actieve bronnen" />
        <Counter
          value={data.kpi.changes}
          label="Prijswijzigingen"
          {...(data.kpi.changes > 0 ? { tone: 'danger' as const } : {})}
        />
        <Counter
          value={data.kpi.missing}
          label="Ontbrekende metingen"
          {...(data.kpi.missing > 0 ? { tone: 'warn' as const } : {})}
        />
      </div>

      <div className={styles.filterBar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Zoek op productnaam of artikelnummer"
          defaultValue={zoek}
          onChange={(e) => setFilter({ zoek: e.target.value })}
          aria-label="Zoeken"
        />
        <div className={styles.segments} role="group" aria-label="Filter">
          {[
            { key: 'alle', label: 'Alle' },
            { key: 'gewijzigd', label: `Gewijzigd (${data.kpi.changes})` },
            { key: 'fouten', label: `Fouten (${data.kpi.missing})` },
          ].map((optie) => (
            <button
              key={optie.key}
              type="button"
              className={segment === optie.key ? styles.segmentActive : styles.segment}
              onClick={() => setFilter({ segment: optie.key })}
            >
              {optie.label}
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <div className={styles.segments} role="group" aria-label="Volgorde van de webshops">
          <button
            type="button"
            className={opNaam ? styles.segment : styles.segmentActive}
            onClick={() => setFilter({ sortering: 'prijs' })}
            title="Goedkoopste webshop vooraan, gemeten over alle producten"
          >
            Op prijs
          </button>
          <button
            type="button"
            className={opNaam ? styles.segmentActive : styles.segment}
            onClick={() => setFilter({ sortering: 'naam' })}
          >
            Op naam
          </button>
        </div>
        <Button variant="primary" onClick={() => void exporteer()} busy={exporting}>
          Exporteer naar Excel
        </Button>
      </div>

      {data.products.length === 0 ? (
        <div className={ui.cardPadded}>
          <p className={ui.empty}>
            Geen producten die aan dit filter voldoen. Pas het zoekveld of het filter aan.
          </p>
        </div>
      ) : (
        <>
          <div className={styles.matrixWrap}>
            <table className={styles.matrix}>
              <thead>
                <tr>
                  <th className={styles.productHeader}>Product</th>
                  {ordening.map((index) => {
                    const column = data.columns[index];
                    if (!column) return null;
                    return (
                      <th
                        key={`kolom-${index}`}
                        className={column.isOwn ? styles.ownHeader : undefined}
                      >
                        <BronLabel
                          naam={column.isOwn ? 'Eigen webshop' : column.name}
                          isOwn={column.isOwn}
                          url={column.url}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.products.map((row) => (
                  <tr
                    key={row.productId}
                    className={styles.row}
                    onClick={() => void navigate(`/product/${row.productId}`)}
                  >
                    <td className={styles.productCell}>
                      <div className={styles.productName}>{row.name}</div>
                      <div className={styles.productMeta}>
                        {row.ownSku}
                        {row.lengthLabel ? ` · ${row.lengthLabel}` : ''}
                        {row.color ? ` · ${row.color}` : ''}
                      </div>
                    </td>
                    {ordening.map((index) => {
                      const cell = row.cells[index];
                      return cell ? (
                        <MatrixCell
                          key={`${row.productId}-${index}`}
                          cell={cell}
                          isOwn={data.columns[index]?.isOwn ?? false}
                        />
                      ) : (
                        <td key={`${row.productId}-${index}`} />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={styles.swatchLowest} /> laagste prijs van dat product vandaag
            </span>
            <span className={styles.legendItem}>
              <strong style={{ color: 'var(--c-up)' }}>▲</strong> prijs hoger dan de laatst geldige
              meting
            </span>
            <span className={styles.legendItem}>
              <strong style={{ color: 'var(--c-down)' }}>▼</strong> prijs lager
            </span>
            <span className={styles.legendItem}>
              <strong style={{ color: 'var(--c-empty)' }}>— leeg</strong> meting mislukt; telt niet
              als wijziging
            </span>
            {/*
              De kleuren benoemen, want in de kolom ernaast betekent rood het tegenovergestelde
              (prijs omhoog). Zonder deze regel moet je die omkering zelf ontdekken.
            */}
            <span className={styles.legendItem}>
              percentage = verschil met onze eigen prijs · <strong>rood</strong> = concurrent is
              goedkoper dan wij, <strong>groen</strong> = duurder; hoe sterker de kleur, hoe groter
              het verschil
            </span>
          </div>
        </>
      )}
    </div>
  );
}
